import React, { useRef, useEffect, useState } from 'react';
import { IProps, IState } from './types';
import * as S from './styles';
import C from '@assets/themes/colors';
import { componentToHex, getMidColor, getValueFromLinearFunction, hexToRgba, rgbaToHex } from '@utils/userUtils';
import ColorStopIcon from '@assets/icons/addIcon copy';
import ToolTip from '@components/Tooltip';
import { RGB } from 'ag-psd';
import { IColorStop } from '@routes/private/Editor/types';

const GradientStopsControl: React.FC<IProps> = (props) => {

  const { selectedIndex, setSelectedIndex } = props;
  const gradientBarRef = useRef<HTMLDivElement>(null);

  const [gradientBox, setGradientBox] = useState<IState['gradientBox']>();
  const [grabbing, setGrabbing] = useState(false);
  const [hasMoved, setHasMoved] = useState(false);

  useEffect(() => {
    if (gradientBarRef.current) {
      let rect = gradientBarRef.current.getBoundingClientRect();
      setGradientBox({
        left: rect.x,
        width: rect.width > 0 ? rect.width : 233
      })
    }
  }, [gradientBarRef]);

  const moveColorStop = (notSave: boolean, location?: number) => {
    if (gradientBox?.left && gradientBox?.width) {
      let newColorStops = [...props.colorStops];
      if (location !== undefined && location >= 0 && location <= 1) {
        newColorStops[selectedIndex].location = location;
      }
      props.handleColorStops([...newColorStops], notSave);
    }
  }

  const addColorStop = (index: number, item: IColorStop) => {
    if (gradientBox?.left && gradientBox?.width) {
      let newColorStops = [...props.colorStops];
      newColorStops.splice(index, 0, item);
      setSelectedIndex(index);
      props.handleColorStops([...newColorStops], false);
    }
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (grabbing && gradientBox?.left && gradientBox?.width) {
      setHasMoved(true);
      let left = e.clientX - gradientBox.left;
      if (left < 0) left = 0;
      else if (left > gradientBox.width) left = gradientBox.width;
      moveColorStop(true, left / gradientBox.width);
    }
  }

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    setGrabbing(false);
  }

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>, index: number) => {
    setSelectedIndex(index);
    setGrabbing(true);
  }

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    if (gradientBox?.left && gradientBox?.width) {
      setHasMoved(false);
      setGrabbing(false);
      if (hasMoved)
      moveColorStop(false);
    }
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (gradientBox?.left && gradientBox?.width) {
      let location = e.clientX - gradientBox.left;
      if (location < 0) location = 0;
      else if (location > gradientBox.width) location = gradientBox.width;
      location /= gradientBox.width;
      let color = "";
      let newIndex = 0;
      let opacity: number | undefined = undefined;
      if (props.colorStops?.length) {
        if (props.colorStops.length === 1) {
          color = props.colorStops[0].color;
          opacity = props.colorStops[0].opacity;
          newIndex = location > props.colorStops[0].location ? 1 : 0;
        } else {
          for (let i = 0; i < props.colorStops.length; i ++) {
            const colorStop = props.colorStops[i];
            if (colorStop.location >= location) {
              if (i > 0) {
                const prevColorStop = props.colorStops[i - 1];
                const midPoint = (location - prevColorStop.location) / (colorStop.location - prevColorStop.location);
                const rgbaColor = getMidColor(hexToRgba(prevColorStop.color) as RGB, hexToRgba(colorStop.color) as RGB, midPoint);
                color = rgbaToHex(rgbaColor) ?? "";
                newIndex = i;
                opacity = getValueFromLinearFunction(prevColorStop.location, prevColorStop.opacity ?? 1, colorStop.location, colorStop.opacity ?? 1, location);
                break;
              } else {
                const midPoint = location / colorStop.location;
                const rgbaColor = getMidColor(hexToRgba(colorStop.color) as RGB, hexToRgba(colorStop.color) as RGB, midPoint);
                color = rgbaToHex(rgbaColor) ?? "";
                newIndex = i;
                opacity = getValueFromLinearFunction(0, colorStop.opacity ?? 1, colorStop.location, colorStop.opacity ?? 1, location);
                break;
              }
            } else if (i === props.colorStops.length - 1) {
              color = colorStop.color
              newIndex = i + 1;
              opacity = colorStop.opacity;
            }
          }
        }
      }
      if (opacity) {
        if (opacity < 0) opacity = 0;
        else if (opacity > 1) opacity = 1;
      }
      if (color) {
        const newColorStop: IColorStop = {
          color,
          location,
          opacity,
        };
        addColorStop(newIndex, newColorStop);
      }
    }
  }

  const getBackgroundGradient = () => {
    let gradient = "linear-gradient(90deg"
    props.colorStops.forEach(colorStop => {
      gradient += `, ${colorStop.color}${componentToHex((colorStop.opacity ?? 1) * 255)} ${colorStop.location * 100}%`;
    });
    return gradient += ")";
  }

  return (
    <S.Root onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} >
      <S.GradientContainer>
        <ToolTip
          tip={'Click anywhere in this gradient \npreview to add a new color'}
          boxStyle={{top: '-45px'}}
        >
          <S.Gradient
            ref={gradientBarRef}
            backgroundValue={getBackgroundGradient()}
            backgroundColorValue={props.colorStops.length === 1 ? `${props.colorStops[0].color}${componentToHex((props.colorStops[0].opacity ?? 1) * 255)}` : undefined}
            onClick={handleClick}
          />
        </ToolTip>
      </S.GradientContainer>
      <S.ColorStopsContainer cursorValue={grabbing ? 'grabbing' : undefined} >
        {
          props.colorStops.map((colorStop, index) => {
            return (
              <S.ColorStopContainer
                key={index}
                borderColorValue={index === selectedIndex ? C.BLUE : C.LIGHTER_GRAY}
                leftValue={`${(colorStop.location * (gradientBox?.width ?? 233))}px`}
                cursorValue={grabbing ? 'grabbing' : 'grab'}
                onMouseDown={(e) => handleMouseDown(e, index)}
                onMouseUp={handleMouseUp}
              >
                <ColorStopIcon
                  strokeColor={index === selectedIndex ? C.LIGHTER_BLUE : undefined}
                />
                <S.ColorStop
                  opacityValue={`${colorStop.opacity ?? 1}`}
                  backgroundColorValue={colorStop.color}
                />
              </S.ColorStopContainer>
            )
          })
        }
      </S.ColorStopsContainer>
    </S.Root>
  );
}

export default GradientStopsControl;